/**
 * Environment utilities
 * 
 * This module provides safe access to environment variables in both browser and server environments.
 */

import { appendApiPath, normalizeApiBaseUrl } from './apiUrl';

// OAuth credentials are now managed via backend API calls in backendConfig.ts

/**
 * Safely get environment variables in both browser and Node environments
 * @param key The environment variable key
 * @param defaultValue A default value if the environment variable is not found
 * @returns The environment variable value or the default value
 */
export function getEnv(key: string, defaultValue: string = ''): string {
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [getEnv] Attempting to get key: "${key}"`);
  }

  let value: string | undefined | null = undefined;
  let source: string = 'defaultValue'; // Start assuming default

  // 1. Check process.env (build-time injected for CRA)
  // @ts-ignore - process may or may not be available
  const processEnvValue = typeof process !== 'undefined' && typeof process.env !== 'undefined' ? process.env[key] : undefined;
  if (processEnvValue) {
    value = processEnvValue;
    source = 'process.env';
  }

  // 2. Check window.ENV (runtime, common in Cloudflare but check anyway)
  const windowEnvValue = typeof window !== 'undefined' && window.ENV ? window.ENV[key] : undefined;
  if (!value && windowEnvValue) {
    value = windowEnvValue;
    source = 'window.ENV';
  }

  // 3. OAuth credentials are now fetched from backend API via backendConfig.ts

  // 4. Use defaultValue if no value found yet
  if (value === undefined || value === null || value === '') { // Check more thoroughly if a value was truly found
     if (process.env.NODE_ENV === 'development') {
       console.log(`[${new Date().toISOString()}] [getEnv]   - Using defaultValue: "${defaultValue}"`);
     }
     value = defaultValue;
     source = 'defaultValue'; // Ensure source reflects default if used
  }

  // Ensure we always return a string, handling potential null/undefined from sources
  return value || '';
}

/**
 * Determines if the application is running in a production environment
 * @returns true if in production, false otherwise
 */
export function isProduction(): boolean {
  return getEnv('REACT_APP_ENV', '').toLowerCase() === 'production' ||
         getEnv('NODE_ENV', '').toLowerCase() === 'production' ||
         (typeof window !== 'undefined' && 
          !window.location.hostname.includes('localhost') && 
          !window.location.hostname.includes('127.0.0.1'));
}

function isLoopbackHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
}

function isLoopbackUrl(value: string): boolean {
  if (!value) return false;
  try {
    const url = value.startsWith('http')
      ? new URL(value)
      : typeof window !== 'undefined'
        ? new URL(value, window.location.origin)
        : null;
    return url ? isLoopbackHost(url.hostname) : false;
  } catch {
    return false;
  }
}

function shouldIgnoreLoopback(value: string): boolean {
  if (typeof window === 'undefined') return false;
  if (isLoopbackHost(window.location.hostname)) return false;
  return isLoopbackUrl(value);
}

/**
 * Get the base URL for API calls based on current environment.
 *
 * The returned URL always includes the `/api` prefix and never has a
 * trailing slash, so callers can safely append paths like `/campaigns`.
 *
 * @returns The base API URL (e.g. "https://host.com/api")
 */
export function getApiBaseUrl(): string {
  const apiUrl = getEnv('REACT_APP_API_ENDPOINT', '');
  if (apiUrl) {
    if (shouldIgnoreLoopback(apiUrl)) {
      console.warn('[env] Ignoring loopback REACT_APP_API_ENDPOINT on non-local host:', apiUrl);
    } else {
      return normalizeApiBaseUrl(apiUrl);
    }
  }

  // REACT_APP_API_ENDPOINT wins over the older BASE_URL/PREFIX pair.
  // PRD001 freezes ordinary HTTP routing at /api; custom prefix values are
  // deliberately ignored while BASE_URL remains a supported origin override.
  const configuredBaseUrl = getEnv('REACT_APP_API_BASE_URL', '');
  if (configuredBaseUrl && !shouldIgnoreLoopback(configuredBaseUrl)) {
    return normalizeApiBaseUrl(configuredBaseUrl);
  }
  if (getEnv('REACT_APP_ENV', '').toLowerCase() === 'cloudflare') {
    return 'https://mythboundadventurestudio.onrender.com/api';
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isCloudflareHost =
      host === 'mythbound-app.pages.dev' ||
      host === 'mythbound.app' ||
      host === 'www.mythbound.app' ||
      host.endsWith('.mythbound.app');
    if (isCloudflareHost) {
      return 'https://mythboundadventurestudio.onrender.com/api';
    }
  }

  if (isProduction()) {
    return `${window.location.origin}/api`;
  }

  return 'http://localhost:8080/api';
}
/** Build an ordinary backend HTTP URL using the canonical one-`/api` base. */
export function buildApiUrl(path: string): string {
  return appendApiPath(getApiBaseUrl(), path);
}

/**
 * Get the appropriate redirect URI for OAuth based on provider and environment
 * @param provider The OAuth provider ('discord' or 'google')
 * @returns The appropriate redirect URI
 */
export function getRedirectUri(provider: 'discord' | 'google'): string {
  const envUri = getEnv(`REACT_APP_${provider.toUpperCase()}_REDIRECT_URI`, '');
  
  if (envUri) return envUri;
  
  if (isProduction()) {
    return `${window.location.origin}/auth/${provider}/callback`;
  }
  
  // Default development URI
  return `http://localhost:3000/auth/${provider}/callback`;
}
